import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import FilledActionButton from 'components/FilledActionButton';
import PageIntro from 'components/PageIntro/PageIntro';
import UIContainer from 'components/ui/UIContainer';
import WithLoader from 'components/WithLoader';
import { useAppSelector } from 'app/hooks';
import { VocabularyTypeRecord } from 'app/types';
import { CORE_META } from './coreMeta';
import { MOCK_CUSTOM_DICTIONARIES } from './mockCustomDictionaries';
import PortActions from './components/PortActions';
import StatPill from './components/StatPill';
import SectionHead from './components/SectionHead';
import CoreDictionaryCard from './components/CoreDictionaryCard';
import CustomDictionaryCard from './components/CustomDictionaryCard';
import AddDictionaryTile from './components/AddDictionaryTile';
import CreateDictionaryModal from './components/CreateDictionaryModal';

const sumVocabulary = (
  data: VocabularyTypeRecord[],
  key: keyof Pick<
    VocabularyTypeRecord,
    'inActiveSpeech' | 'learned' | 'needToLearn' | 'totalCount'
  >,
) => data.reduce((acc, record) => acc + Number(record[key] || 0), 0);

const VocabularyOverview: React.FC = () => {
  const { data, loading } = useAppSelector((store) => store.common.vocabulary);
  const [creating, setCreating] = useState(false);

  const totals = useMemo(
    () => ({
      total: sumVocabulary(data, 'totalCount'),
      learned: sumVocabulary(data, 'learned'),
      due: sumVocabulary(data, 'needToLearn'),
    }),
    [data],
  );

  // Three core sections come back in a fixed order from the BE; pair each
  // with its static metadata (compact name, icon, accent, en label, target
  // URL, copy) for the spotlight cards. We surface the short FE-side
  // `meta.name` instead of the BE's longer `vocabularyName` so the card
  // titles stay one-line at every breakpoint.
  const coreCards = CORE_META.map((meta, index) => {
    const record = data[index];
    if (!record) return null;
    return (
      <CoreDictionaryCard
        key={meta.id}
        meta={meta}
        name={meta.name}
        total={record.totalCount}
        learned={record.learned}
      />
    );
  });

  return (
    <UIContainer sx={{ mb: 8 }}>
      <BreadCrumbs
        actions={
          <>
            <PortActions />
            <FilledActionButton
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={() => setCreating(true)}
            >
              {'Створити словник'}
            </FilledActionButton>
          </>
        }
      />

      <WithLoader loading={loading}>
        <Box>
          <PageIntro
            surface
            title={'Мій словник'}
            titleSx={{ color: 'primary.main' }}
            description={
              'Усі слова, які ти зберігаєш, — згруповані за наборами. Повторюй, відстежуй прогрес і створюй власні словники.'
            }
            aside={
              <StatPill
                total={totals.total}
                learned={totals.learned}
                due={totals.due}
              />
            }
          />

          {/* Core dictionaries */}
          <SectionHead count={CORE_META.length}>
            {'Основні словники'}
          </SectionHead>
          <Box
            sx={{
              display: 'grid',
              gap: 2.25,
              // `minmax(0, 1fr)` lets columns shrink below intrinsic min
              // content width so the nowrap+ellipsis title rule actually
              // truncates instead of pushing the whole page horizontally.
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              mb: 4.75,
            }}
          >
            {coreCards}
          </Box>

          {/* Custom dictionaries — mock until BE ships persistence */}
          <SectionHead count={MOCK_CUSTOM_DICTIONARIES.length}>
            {'Мої словники'}
          </SectionHead>
          <Box
            sx={{
              display: 'grid',
              gap: 2.25,
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
              },
            }}
          >
            {MOCK_CUSTOM_DICTIONARIES.map((dict) => (
              <CustomDictionaryCard key={dict.id} dict={dict} />
            ))}
            <AddDictionaryTile onClick={() => setCreating(true)} />
          </Box>
        </Box>
      </WithLoader>

      <CreateDictionaryModal
        open={creating}
        onClose={() => setCreating(false)}
      />
    </UIContainer>
  );
};

export default VocabularyOverview;
